import { createSlice } from "@reduxjs/toolkit";
import { fetchStatus } from "./preferenceSlice";

import {
  onDeleteExistingMapping,
  onSaveEditedMappings,
  onDeleteSelectedMappings,
  onAddHsFieldsToCustomMappings,
} from "./mappingSlice";

const initialState = {
  preferencesForProducts: "do not generate invoice",
  productPreference: undefined,
  preferenceForSearchParameters: "select search parameters",
  txProductFilterPreference: undefined,
  hubspotProductFilterPreference: undefined,
  isProductSyncRulesSaved: false,
  preferenceForCreatingProduct: false,
  defaultProductForCreating: undefined,
  productCurrencyPreference: undefined,
  productDepartmentPreference: undefined,
  productVolumePreference: undefined,
  productUnitPreference: undefined,
  productWeightPreference: undefined,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    /**
     * Reducer updates the state of radio buttons for products
     */
    onChangePreferenceForProducts(state, { payload }) {
      state.preferencesForProducts = payload.selected;
      if (state.preferencesForProducts !== "find create product") {
        state.preferenceForCreatingProduct = false;
        state.defaultProductForCreating = undefined;
      }
      if (state.preferencesForProducts !== "use default product") {
        state.productPreference = undefined;
      }
      state.isProductSyncRulesSaved = false;
    },
    /**
     * Reducer updates the state of radio buttons for search parameters
     */
    onChangePreferenceForSearchParameters(state, { payload }) {
      state.preferenceForSearchParameters = payload.selected;
      state.isProductSyncRulesSaved = false;
    },
    /**
     * Reducer updates that state for checkbox of default economic product
     */
    onChangePreferenceForCreatingProduct(state) {
      state.preferenceForCreatingProduct = !state.preferenceForCreatingProduct;
      if (state.preferenceForCreatingProduct === false) {
        state.defaultProductForCreating = undefined;
      }
      state.isProductSyncRulesSaved = false;
    },
    /**
     * Reducer updates the state for the invoice tab dropdown fields
     */
    setSelectedProductFields(state, { payload }) {
      if (payload.selectingFor === "products") {
        state.productPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "txProductFilterField") {
        state.txProductFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "hubspotProductFilterField") {
        state.hubspotProductFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "defaultProduct") {
        state.defaultProductForCreating = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "productCurrency") {
        state.productCurrencyPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
      } else if (payload.selectingFor === "productDepartment") {
        state.productDepartmentPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
      } else if (payload.selectingFor === "productWeight") {
        state.productWeightPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
      } else if (payload.selectingFor === "productUnit") {
        state.productUnitPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
      } else if (payload.selectingFor === "productVolume") {
        state.productVolumePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
      }
      state.isProductSyncRulesSaved = false;
    },
    setIsProductSyncRulesSaved(state) {
      state.isProductSyncRulesSaved = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      const { preference, mapping } = action.payload;

      if (preference !== undefined && Object.keys(preference).length > 0) {
        const { product } = preference;

        if (product?.generateOrder) {
          state.preferencesForProducts =
            product.generateOrder?.create === true
              ? "use default product"
              : "do not generate invoice";

          state.productPreference = product.generateOrder?.mapping
            ?.txDefaultProduct?.value
            ? product.generateOrder?.mapping?.txDefaultProduct
            : undefined;
        }

        if (product?.createProduct) {
          state.preferencesForProducts = product?.createProduct?.create
            ? "find create product"
            : product?.generateOrder?.create
            ? "use default product"
            : "do not generate invoice";

          state.preferenceForCreatingProduct =
            product?.createProduct?.customProduct?.useTxDefaultProduct;

          state.defaultProductForCreating = product?.createProduct
            ?.customProduct?.mapping?.txDefaultProduct?.value
            ? product?.createProduct?.customProduct?.mapping?.txDefaultProduct
            : undefined;
        }

        state.preferenceForSearchParameters =
          product.searchParameters === false || product.searchParameters === ""
            ? "select search parameters"
            : "set custom mappings";

        if (product?.filterOptions) {
          state.hubspotProductFilterPreference = product.filterOptions
            ?.filterHsProperty?.value
            ? product.filterOptions?.filterHsProperty
            : undefined;
          state.txProductFilterPreference = product.filterOptions
            ?.filterTxProperty?.value
            ? product.filterOptions?.filterTxProperty
            : undefined;
        }

        // if (product?.txFields) {
        //   const { currency, department, volume, weight, unit } =
        //     product?.txFields;

        //   state.productCurrencyPreference = currency?.value
        //     ? currency
        //     : undefined;
        //   state.productDepartmentPreference = department?.value
        //     ? department
        //     : undefined;
        //   state.productUnitPreference = unit?.value ? unit : undefined;
        //   state.productVolumePreference = volume?.value ? volume : undefined;
        //   state.productWeightPreference = weight?.value ? weight : undefined;
        // }
      }

      if (
        ((state.preferencesForProducts === "use default product" &&
          state.productPreference) ||
          state.preferencesForProducts === "do not generate invoice" ||
          (state.preferencesForProducts === "find create product" &&
            (!state.preferenceForCreatingProduct ||
              (state.preferenceForCreatingProduct &&
                state.defaultProductForCreating)))) &&
        ((state.preferenceForSearchParameters === "select search parameters" &&
          state.hubspotProductFilterPreference &&
          state.txProductFilterPreference) ||
          (state.preferenceForSearchParameters === "set custom mappings" &&
            mapping?.product?.length > 0))
      ) {
        state.isProductSyncRulesSaved = true;
      }
    });

    builder.addCase(onDeleteExistingMapping, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "product"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });
    builder.addCase(onSaveEditedMappings, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "product"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });
    builder.addCase(onDeleteSelectedMappings, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "product"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });
    builder.addCase(onAddHsFieldsToCustomMappings, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "product"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });
  },
});

export default productSlice.reducer;
export const {
  onChangePreferenceForProducts,
  onChangePreferenceForSearchParameters,
  setSelectedProductFields,
  setIsProductSyncRulesSaved,
  onChangePreferenceForCreatingProduct,
} = productSlice.actions;
